import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Plus,
  Trash2,
  X,
  Clock,
  AlertTriangle,
  ExternalLink,
  TrendingUp,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { listAccounts, type ConnectedAccount } from '../api/analytics';
import { trackerHistory } from '../api/rankTracker';
import {
  cancelScheduledPin,
  createSlot,
  deleteSlot,
  listBoards,
  listScheduledPins,
  listSlots,
  schedulePin,
  type ScheduledPin,
  type ScheduledPinStatus,
} from '../api/scheduler';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

function minuteToHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function hhmmToMinute(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function fmtDayHeader(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function SchedulerPage() {
  const { token } = useAuth();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const accountsQuery = useQuery({
    queryKey: ['scheduler', 'accounts'],
    queryFn: () => listAccounts(token),
  });

  // Default to the first account once loaded.
  const accounts = accountsQuery.data ?? [];
  const activeAccountId = accountId ?? accounts[0]?.id ?? null;

  return (
    <>
      <PageHeader
        icon={CalendarClock}
        title="Scheduler"
        description="Plan your posting times, then queue pins to publish automatically."
      />

      {accountsQuery.isError && (
        <ErrorAlert message={errMessage(accountsQuery.error, 'Failed to load accounts.')} />
      )}

      {accountsQuery.isPending && (
        <div className="py-16 text-center text-sm text-ink-400">Loading accounts…</div>
      )}

      {accountsQuery.isSuccess && accounts.length === 0 && (
        <EmptyState
          icon={CalendarClock}
          title="Connect a Pinterest account first"
          description="Scheduling publishes to a connected account. Head to Analytics to connect one, then come back to plan your posts."
        />
      )}

      {activeAccountId && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            {accounts.length > 1 && (
              <select
                value={activeAccountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                {accounts.map((a: ConnectedAccount) => (
                  <option key={a.id} value={a.id}>
                    @{a.username}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              <Plus className="h-4 w-4" /> Schedule a pin
            </button>
          </div>

          <SlotManager accountId={activeAccountId} token={token} />
          <ScheduledList accountId={activeAccountId} token={token} />
        </div>
      )}

      {drawerOpen && activeAccountId && (
        <ScheduleDrawer
          accountId={activeAccountId}
          token={token}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}

function SlotManager({ accountId, token }: { accountId: string; token: string | null }) {
  const qc = useQueryClient();
  const [weekday, setWeekday] = useState(1);
  const [time, setTime] = useState('09:00');

  const slotsQuery = useQuery({
    queryKey: ['scheduler', 'slots', accountId],
    queryFn: () => listSlots(accountId, token),
  });

  const add = useMutation({
    mutationFn: () =>
      createSlot(
        accountId,
        { weekday, minuteOfDay: hhmmToMinute(time), timezone: BROWSER_TZ },
        token,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduler', 'slots', accountId] }),
  });

  const del = useMutation({
    mutationFn: (slotId: string) => deleteSlot(slotId, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduler', 'slots', accountId] }),
  });

  const slots = slotsQuery.data ?? [];

  return (
    <div className="rounded-lg border border-ink-200 bg-white shadow-sm">
      <div className="border-b border-ink-100 px-6 py-4">
        <h2 className="text-sm font-semibold text-ink-900">Posting plan</h2>
        <p className="text-xs text-ink-500">
          Recurring weekly slots. “Add to queue” drops a pin in the next open one. Times are in{' '}
          <span className="font-medium">{BROWSER_TZ}</span>.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-6 py-3">
        <select
          value={weekday}
          onChange={(e) => setWeekday(Number(e.target.value))}
          className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
        >
          {WEEKDAYS.map((d, i) => (
            <option key={d} value={i}>
              {d}
            </option>
          ))}
        </select>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
        />
        <button
          onClick={() => add.mutate()}
          disabled={add.isPending}
          className="inline-flex items-center gap-1.5 rounded-md border border-ink-200 px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Add slot
        </button>
        {add.isError && (
          <span className="text-xs text-brand-600">
            {errMessage(add.error, 'Could not add slot')}
          </span>
        )}
      </div>

      <div className="px-6 py-4">
        {slotsQuery.isPending ? (
          <p className="text-sm text-ink-400">Loading slots…</p>
        ) : slots.length === 0 ? (
          <p className="text-sm text-ink-500">
            No slots yet — add a few to build a weekly posting rhythm.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-ink-50 py-1 pl-3 pr-1.5 text-sm"
              >
                <Clock className="h-3.5 w-3.5 text-ink-400" />
                {WEEKDAYS[s.weekday]} {minuteToHHMM(s.minuteOfDay)}
                <button
                  onClick={() => del.mutate(s.id)}
                  disabled={del.isPending}
                  className="rounded-full p-0.5 text-ink-400 hover:bg-ink-200 hover:text-ink-700"
                  title="Remove slot"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduledList({ accountId, token }: { accountId: string; token: string | null }) {
  const pinsQuery = useQuery({
    queryKey: ['scheduler', 'pins', accountId],
    queryFn: () => listScheduledPins({ accountId }, token),
  });

  const grouped = useMemo(() => {
    const pins = pinsQuery.data ?? [];
    const map = new Map<string, ScheduledPin[]>();
    for (const p of pins) {
      const k = dayKey(p.scheduledFor);
      const arr = map.get(k) ?? [];
      arr.push(p);
      map.set(k, arr);
    }
    // Map preserves insertion order; pins arrive sorted by scheduledFor asc.
    return [...map.entries()];
  }, [pinsQuery.data]);

  if (pinsQuery.isPending) {
    return <div className="py-8 text-center text-sm text-ink-400">Loading scheduled pins…</div>;
  }
  if (pinsQuery.isError) {
    return <ErrorAlert message={errMessage(pinsQuery.error, 'Failed to load scheduled pins.')} />;
  }
  if (grouped.length === 0) {
    return (
      <EmptyState
        icon={CalendarClock}
        title="Nothing scheduled yet"
        description="Add posting slots above, then click “Schedule a pin” to queue your first post."
      />
    );
  }

  return (
    <div className="space-y-5">
      {grouped.map(([key, pins]) => (
        <div key={key}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
            {fmtDayHeader(pins[0].scheduledFor)}
          </h3>
          <div className="space-y-2">
            {pins.map((p) => (
              <PinCard key={p.id} pin={p} accountId={accountId} token={token} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PinCard({
  pin,
  accountId,
  token,
}: {
  pin: ScheduledPin;
  accountId: string;
  token: string | null;
}) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => cancelScheduledPin(pin.id, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduler', 'pins', accountId] }),
  });

  return (
    <div className="flex items-center gap-4 rounded-lg border border-ink-200 bg-white p-3 shadow-sm">
      <img
        src={pin.imageUrl}
        alt={pin.altText ?? ''}
        className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink-900">{pin.title}</span>
          <StatusChip status={pin.status} />
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
          <Clock className="h-3 w-3" /> {fmtTime(pin.scheduledFor)}
          {pin.publishedPinId && (
            <a
              href={`https://www.pinterest.com/pin/${pin.publishedPinId}/`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-brand-500 hover:underline"
            >
              View pin <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {pin.status === 'failed' && pin.lastError && (
          <div className="mt-1 flex items-start gap-1 text-xs text-brand-600">
            <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
            <span className="line-clamp-2">{pin.lastError}</span>
          </div>
        )}
        <RankReadout pin={pin} token={token} />
      </div>
      {(pin.status === 'scheduled' || pin.status === 'failed') && (
        <button
          onClick={() => {
            if (window.confirm(`Cancel "${pin.title}"?`)) cancel.mutate();
          }}
          disabled={cancel.isPending}
          className="rounded-md p-1.5 text-ink-500 hover:bg-brand-50 hover:text-brand-600 disabled:opacity-50"
          title="Cancel"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/**
 * The differentiator, made visible: for a published pin with a target keyword, show whether it
 * actually ranked. Scheduled pins show the pending intent. Pinterest's scheduler shows neither.
 */
function RankReadout({ pin, token }: { pin: ScheduledPin; token: string | null }) {
  const enabled = pin.status === 'published' && !!pin.rankTrackerId;
  const historyQuery = useQuery({
    queryKey: ['rank-tracker', pin.rankTrackerId, 'history'],
    queryFn: () => trackerHistory(pin.rankTrackerId!, token),
    enabled,
    refetchOnWindowFocus: false,
  });

  // Not yet published: show the intent so the user knows the loop is armed.
  if (pin.status !== 'published') {
    if (!pin.targetKeyword) return null;
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-ink-400">
        <TrendingUp className="h-3 w-3" />
        Will track rank for “{pin.targetKeyword}”
      </div>
    );
  }

  if (!enabled) return null;

  const latest = historyQuery.data?.[0];
  const label = pin.targetKeyword ? `“${pin.targetKeyword}”` : 'keyword';

  if (historyQuery.isPending) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-ink-400">
        <TrendingUp className="h-3 w-3" /> Checking rank for {label}…
      </div>
    );
  }
  if (!latest) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-ink-500">
        <TrendingUp className="h-3 w-3" /> Tracking rank for {label} — first check pending
      </div>
    );
  }
  if (latest.position === null) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-ink-500">
        <TrendingUp className="h-3 w-3" /> Not in top {latest.totalResults ?? 100} for {label} yet
      </div>
    );
  }
  return (
    <div className="mt-1 flex items-center gap-1 text-xs font-medium text-emerald-700">
      <TrendingUp className="h-3 w-3" /> Ranked #{latest.position} for {label}
    </div>
  );
}

function StatusChip({ status }: { status: ScheduledPinStatus }) {
  const styles: Record<ScheduledPinStatus, string> = {
    scheduled: 'bg-ink-100 text-ink-700',
    publishing: 'bg-amber-50 text-amber-700',
    published: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-brand-50 text-brand-700',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function ScheduleDrawer({
  accountId,
  token,
  onClose,
}: {
  accountId: string;
  token: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [boardId, setBoardId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [link, setLink] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [mode, setMode] = useState<'queue' | 'explicit'>('queue');
  const [when, setWhen] = useState('');

  const boardsQuery = useQuery({
    queryKey: ['scheduler', 'boards', accountId],
    queryFn: () => listBoards(accountId, token),
  });

  const mut = useMutation({
    mutationFn: () =>
      schedulePin(
        {
          pinterestAccountId: accountId,
          pinterestBoardId: boardId,
          title: title.trim(),
          description: description.trim() || undefined,
          link: link.trim() || undefined,
          imageUrl: imageUrl.trim(),
          targetKeyword: targetKeyword.trim() || undefined,
          scheduledFor:
            mode === 'explicit' && when ? new Date(when).toISOString() : undefined,
          timezone: BROWSER_TZ,
        },
        token,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduler', 'pins', accountId] });
      onClose();
    },
  });

  const boards = boardsQuery.data ?? [];
  const canSubmit =
    boardId && title.trim() && imageUrl.trim() && (mode === 'queue' || when);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex justify-end bg-ink-900/40"
      aria-modal="true"
      role="dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-lg"
      >
        <div className="mb-5 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-ink-900">Schedule a pin</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="Board">
            {boardsQuery.isPending ? (
              <p className="text-sm text-ink-400">Loading boards…</p>
            ) : boards.length === 0 ? (
              <p className="text-sm text-ink-500">
                No boards found for this account.
              </p>
            ) : (
              <select
                value={boardId}
                onChange={(e) => setBoardId(e.target.value)}
                className="w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Select a board…</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.pinterestBoardId}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Title">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>

          <Field label="Image URL" hint="Must stay publicly reachable until publish time.">
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>

          <Field label="Destination link" hint="Optional.">
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>

          <Field label="Description" hint="Optional.">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={800}
              rows={3}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>

          <Field
            label="Target keyword"
            hint="We'll track whether this pin ranks for it after publishing — the payoff Pinterest's scheduler can't give you. Optional."
          >
            <input
              value={targetKeyword}
              onChange={(e) => setTargetKeyword(e.target.value)}
              placeholder='e.g. "minimalist wedding invitations"'
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Field>

          <Field label="When">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode('queue')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  mode === 'queue'
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-ink-200 text-ink-700'
                }`}
              >
                Next open slot
              </button>
              <button
                type="button"
                onClick={() => setMode('explicit')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  mode === 'explicit'
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-ink-200 text-ink-700'
                }`}
              >
                Pick a time
              </button>
            </div>
            {mode === 'explicit' && (
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="mt-2 w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            )}
          </Field>

          {mut.isError && (
            <ErrorAlert message={errMessage(mut.error, 'Could not schedule pin.')} />
          )}

          <button
            onClick={() => mut.mutate()}
            disabled={!canSubmit || mut.isPending}
            className="w-full rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {mut.isPending ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
    </div>
  );
}
